/**
 * Who may mark a task complete.
 *
 * Editing a task follows the usual owner-or-partner access rules, but declaring
 * work done is narrower: only the person who raised the task, the person it is
 * assigned to, and the owner of the container it hangs off (project, location
 * or portfolio) may do it. The server enforces the same rule in
 * `backend/utilities/taskCompletionAccess.js`; this mirror exists so the UI can
 * hide the control rather than let a click fail with a 403.
 */

/** Read an id whether the field arrived populated, as an id string, or as null. */
const idOf = (value: any): string => {
	if (!value) return ''
	if (typeof value === 'string') return value
	return String(value._id || value.id || value)
}

/**
 * @param task the task (populated or not)
 * @param userId the logged-in user's id
 * @param containerOwners extra owner references the screen already knows about —
 *   e.g. the location or portfolio the page is scoped to, which the task itself
 *   does not carry in populated form.
 */
export function canCompleteTask(task: any, userId?: string, containerOwners: any[] = []): boolean {
	if (!task || !userId) return false

	const me = String(userId)
	const candidates = [
		task.userid,
		task.createdBy,
		task.assignedTo,
		task.projectid?.owner,
		task.projectid?.projectAsset?.owner,
		task.packageid?.owner,
		task.portfolioid?.owner,
		...containerOwners,
	]

	return candidates.some(candidate => idOf(candidate) === me)
}

/** Message shown when someone without the right tries to complete a task. */
export const COMPLETE_TASK_DENIED =
	'Only the task creator, the assignee, or the owner can mark this task complete.'
